import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, Grid, lighten, Typography, useTheme, Tooltip } from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AuditorDetailsForExport from "./AuditNotesActions/AuditorDetailsForExport/AuditorDetailsForExport";
import AuditStatusChange from "./AuditNotesActions/AuditStatusChange/AuditStatusChange";
import DeleteTravelExpense from "./DeleteTravelExpense/DeleteTravelExpense";
import { Props } from "./interfaces";
import ReceiptStatus from "./ReceiptComponents/ReceiptStatus/ReceiptStatus";
import TypeAndDescription from "./ReceiptComponents/TypeAndDescription/TypeAndDescription";
import {
  TravelExpenseWrapper,
  TravelExpenseContainer,
  DescriptionBlock,
  AmountBlock,
  NotesInfoContainer,
  IconTextContainer,
  FlagIconContainer,
  AmountsContainer,
  AmountRow,
  AmountsTitleContainer,
  AmountTitle,
  Amounts,
  ReceiptStatusContainer,
  StatusFlagContainer,
  MainInfoContainer,
} from "./styles";
import { BellIcon } from "../../../../../../../assets/icons";
import FlagIcon from "../../../../../../../containers/FlagIcon/FlagIcon";
import { useAppSelector } from "../../../../../../../custom-hooks/redux/redux";
import { isAuthorizedEeGroup } from "../../../../../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../../../../../../utils/constants/authorization-constants";
import { EXPENSE_REPORT_STATUS } from "../../../../../../../utils/constants/constants";
import { ExpenseReportView } from "../../../../utils";

const TravelExpenseReciept = ({
  travelExpense,
  view,
  expenseReportStatusId,
  handleChange,
  allTravelExpenses,
  readOnly,
  employee,
  handleNotifyParent,
  hideAuditNotes,
  preloadedFlags,
  showAuditorNotes = false,
}: Props) => {
  const theme = useTheme();
  const tRP = "components.expense_reports";
  const { t } = useTranslation();
  const { currentUserAuthorizations, currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const isAuditor = useMemo(() => {
    return isAuthorizedEeGroup(
      [AUTHORIZATIONS.EmployeesAccess.AuditTravelAndExpenses],
      currentUserAuthorizations,
      employee?.groups?.map((group) => group?.id) ?? [],
      true
    );
  }, [currentUserAuthorizations, employee?.groups]);

  const hasAuditNotesNotification = useMemo(() => {
    return (
      !!travelExpense.auditNotes.length &&
      travelExpense.auditNotes[0]?.createdByUser?.id !== currentUserData?.user?.id &&
      !travelExpense.auditNotes[0].travelExpenseAuditNoteReviews.find(
        (preview) => preview.createdByUser?.id === currentUserData?.user?.id
      )
    );
  }, [currentUserData, travelExpense]);

  const questionRaisedByAuditor = useMemo(() => {
    return (
      isAuditor &&
      view !== ExpenseReportView.employee &&
      !!travelExpense.auditNotes.length &&
      travelExpense.auditNotes[0]?.createdByUser?.id === currentUserData?.user?.id
    );
  }, [currentUserData, travelExpense, isAuditor, view]);

  const handleRemoveTravelExpense = useCallback(() => {
    const newTravelExpenses = allTravelExpenses.filter((item) => item?.id !== travelExpense?.id);

    handleChange && handleChange("travelExpenses", newTravelExpenses);
  }, [travelExpense, allTravelExpenses, handleChange]);

  return (
    <TravelExpenseWrapper>
      <TravelExpenseContainer auditAccepted={travelExpense.auditAccepted}>
        <MainInfoContainer
          flex={3}
          highlight={hasAuditNotesNotification || questionRaisedByAuditor}
        >
          {(hasAuditNotesNotification || questionRaisedByAuditor) && !hideAuditNotes && (
            <NotesInfoContainer flex={1}>
              <IconTextContainer>
                <BellIcon
                  width="15px"
                  height="15px"
                  fill={theme.palette.error.main}
                />

                <Typography sx={{ color: theme.palette.error.main }}>
                  {!questionRaisedByAuditor
                    ? view === ExpenseReportView.employee || (view === ExpenseReportView.manager && !isAuditor)
                      ? `${t(`${tRP}.question_from_auditor`)}`
                      : `${t(`${tRP}.pending_response`)}`
                    : `${t(`${tRP}.raised_a_question`)}`}
                </Typography>
              </IconTextContainer>
            </NotesInfoContainer>
          )}
          <Grid style={{ display: "flex" }}>
            <DescriptionBlock container>
              <TypeAndDescription
                type={travelExpense.type?.name ?? ""}
                flex={1}
                typeIconContent={travelExpense?.type?.iconContent}
                date={travelExpense.date}
                description={travelExpense.description}
              />
            </DescriptionBlock>

            <AmountBlock>
              <AmountsContainer>
                <AmountRow>
                  <AmountsTitleContainer>
                    <AmountTitle>{`${t(`${tRP}.amount`)}`}:</AmountTitle>
                  </AmountsTitleContainer>

                  <Amounts
                    title={`${t(`${tRP}.amount`)} : ${travelExpense.amount.toLocaleCustom()} ${
                      travelExpense.currency?.code ?? ""
                    }`}
                  >
                    {`${travelExpense.amount.toLocaleCustom()} ${travelExpense.currency?.code ?? ""}`}
                  </Amounts>
                </AmountRow>

                <AmountRow>
                  <AmountsTitleContainer>
                    <AmountTitle>{`${t(`${tRP}.accepted_amount`)}`}:</AmountTitle>
                  </AmountsTitleContainer>

                  <Amounts
                    aceeptedAmount
                    title={`${t(`${tRP}.accepted_amount`)}: ${travelExpense.acceptedAmount.toLocaleCustom()} ${
                      travelExpense.currency?.code ?? ""
                    }`}
                  >{`${travelExpense.acceptedAmount.toLocaleCustom()} ${travelExpense.currency?.code ?? ""}`}</Amounts>
                </AmountRow>
              </AmountsContainer>
            </AmountBlock>
          </Grid>
        </MainInfoContainer>

        <Divider
          orientation="vertical"
          flexItem
          style={{
            width: "5px",
            border: "none",
          }}
        />

        <StatusFlagContainer auditAccepted={travelExpense.auditAccepted}>
          <ReceiptStatusContainer>
            <ReceiptStatus auditAccepted={travelExpense.auditAccepted} />

            {isAuditor &&
              expenseReportStatusId !== EXPENSE_REPORT_STATUS.approved.id &&
              expenseReportStatusId !== EXPENSE_REPORT_STATUS.paid.id &&
              expenseReportStatusId !== EXPENSE_REPORT_STATUS.rejected.id &&
              expenseReportStatusId !== EXPENSE_REPORT_STATUS.returned_to_employee.id && (
                <AuditStatusChange
                  item={travelExpense}
                  handleNotifyParent={handleNotifyParent}
                />
              )}
          </ReceiptStatusContainer>
          {isAuditor && (
            <Grid style={{ display: "flex", gap: "5px" }}>
              <Tooltip
                title={`${t(`${tRP}.was_the_cost_really_incurred`)} :
                      ${
                        travelExpense.costIncurredComment
                          ? travelExpense.costIncurredComment
                          : `${t(`${tRP}.no_comment_added`)}`
                      }`}
              >
                <span>
                  {travelExpense.costIncurred ? (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      color={theme.palette.success.main}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      color={lighten(theme.palette.secondary.light, 0.4)}
                    />
                  )}
                </span>
              </Tooltip>

              <Tooltip
                title={` ${t(`${tRP}.amount_and_expense_type_justified`)} :
                      ${
                        travelExpense.amountAndTypeJustifiedComment
                          ? travelExpense.amountAndTypeJustifiedComment
                          : "No comment added"
                      }`}
              >
                <span>
                  {travelExpense.amountAndTypeJustified ? (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      color={theme.palette.success.main}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      color={lighten(theme.palette.secondary.light, 0.4)}
                    />
                  )}
                </span>
              </Tooltip>
              <Tooltip
                title={` ${t(`${tRP}.is_receipt_readable`)}  :
                      ${
                        travelExpense.compliantReceiptComment
                          ? travelExpense.compliantReceiptComment
                          : `${t(`${tRP}.no_comment_added`)}`
                      }`}
              >
                <span>
                  {travelExpense.compliantReceipt ? (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      color={theme.palette.success.main}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      color={lighten(theme.palette.secondary.light, 0.4)}
                    />
                  )}
                </span>
              </Tooltip>
            </Grid>
          )}
          <FlagIconContainer
            flex={1}
            title={travelExpense.country?.name ?? ""}
          >
            {!!travelExpense.country?.code && (
              <FlagIcon
                countryCode={travelExpense.country.code}
                customStyles={{ borderRadius: "8px", width: "50px", height: "38px" }}
                predefinedSRC={preloadedFlags?.[travelExpense.country.code]}
              />
            )}
          </FlagIconContainer>
        </StatusFlagContainer>

        <>{showAuditorNotes && <AuditorDetailsForExport travelExpense={travelExpense} />}</>
      </TravelExpenseContainer>

      {view === ExpenseReportView.employee &&
        (expenseReportStatusId === EXPENSE_REPORT_STATUS.draft.id ||
          expenseReportStatusId === EXPENSE_REPORT_STATUS.returned_to_employee.id) &&
        !readOnly && <DeleteTravelExpense handleNotifyParent={handleRemoveTravelExpense} />}
    </TravelExpenseWrapper>
  );
};

export default TravelExpenseReciept;
